import { z } from "zod";

/**
 * Art used on exported cards is described by a manifest rather than hardcoded
 * paths, so adding a portrait is a data change.
 *
 * Two constraints worth remembering when adding entries:
 *  - `src` must be served from the same origin as the app. A cross-origin
 *    image drawn into the export canvas taints it and PNG export will throw.
 *  - `license` is not decorative. If any of this art is third-party and cards
 *    get shared publicly, this is the field that says whether that is allowed.
 */
export const artAssetSchema = z.object({
  id: z.string().min(1),
  displayName: z.string().min(1),
  /** App-relative path, e.g. "/art/portraits/drifter-01.webp". */
  src: z.string().startsWith("/"),
  kind: z.enum(["portrait", "flag", "frame", "backdrop"]),
  /** Optional affinity used to sort/filter the picker. */
  factionId: z.string().optional(),
  tags: z.array(z.string()).default([]),
  attribution: z
    .object({
      artist: z.string().optional(),
      source: z.string().optional(),
      license: z.string(),
    })
    .optional(),
});

export type ArtAsset = z.infer<typeof artAssetSchema>;

export const artManifestSchema = z.array(artAssetSchema);

export function assetsOfKind(manifest: ArtAsset[], kind: ArtAsset["kind"]): ArtAsset[] {
  return manifest.filter((asset) => asset.kind === kind);
}

export function assetsForFaction(manifest: ArtAsset[], factionId: string): ArtAsset[] {
  return manifest.filter((asset) => asset.factionId === undefined || asset.factionId === factionId);
}
